// ── AR Flow — NOVA INTERFACE, Etapa 8a: Vendas ────────────────────────────────
// Todas as vendas num lugar só, de todas as origens (Lead, Presencial,
// Prospecção, Indicação). Duas visões: vendas do mês e acompanhamento.
// Só visual: recebe os dados prontos e chama "abrir" para ir à conversa.

import { useState } from "react";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const DISPLAY = "'Bricolage Grotesque', 'Source Sans 3', system-ui, sans-serif";
const brl = v => (Number(v) || 0).toLocaleString("pt-BR", { style:"currency", currency:"BRL", maximumFractionDigits:0 });

export function dataCurta(v) {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    const [year, month, day] = v.slice(0, 10).split("-");
    return `${day}/${month}/${year.slice(2)}`;
  }
  const date = new Date(v);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit", year:"2-digit" });
}

const COLUNAS = [
  { id:"aguardando", titulo:"Aguardando entrega", cor:"#B45309" },
  { id:"entregue", titulo:"Entregue", cor:"#1E6B4A" },
  { id:"posvenda", titulo:"Pós-venda", cor:"#6D28D9" },
  { id:"proxima", titulo:"Próxima venda", cor:"#1D4ED8" },
];

export default function Vendas({
  theme, meses, mesIdx, onMes, resumo, vendas, acompanhamento, mostrarConsultor, onAbrir, aviso,
}) {
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const [aba, setAba] = useState("mes");
  const botaoAba = ativo => ({
    border:"none",
    borderBottom:`3px solid ${ativo ? th.accent : "transparent"}`,
    background:"none",
    padding:"10px 4px",
    marginRight:18,
    fontSize:15,
    fontWeight:ativo ? 700 : 500,
    color:ativo ? th.text : th.muted,
    cursor:"pointer",
    fontFamily:"inherit",
  });

  return (
    <div>
      <style>{`
.nu-sales-card {
  transition: background-color 120ms ease, box-shadow 120ms ease;
  -webkit-tap-highlight-color: rgba(184, 150, 12, 0.16);
}
.nu-sales-card:hover {
  background: #FAF8F3 !important;
  box-shadow: inset 0 0 0 1px rgba(184, 150, 12, 0.18);
}
.nu-sales-card:focus-visible {
  outline: 2px solid #B8960C;
  outline-offset: -2px;
  position: relative;
  z-index: 1;
}
.nu-sales-card:active {
  background: #F0EDE6 !important;
}
`}</style>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:10 }}>
        <div style={{ fontFamily:DISPLAY, fontSize:26, fontWeight:700, color:th.text }}>Vendas</div>
        {aba === "mes" && (
          <select aria-label="Mês" value={mesIdx} onChange={event => onMes(Number(event.target.value))} style={{
            padding:"6px 10px", borderRadius:4, border:`1px solid ${line}`, fontFamily:"inherit", fontSize:14,
          }}>
            {meses.map((month, index) => <option key={index} value={index}>{month}</option>)}
          </select>
        )}
      </div>
      {aba === "mes" && aviso}
      <div role="tablist" style={{ borderBottom:`1px solid ${line}`, marginBottom:16 }}>
        <button role="tab" aria-selected={aba === "mes"} type="button" onClick={() => setAba("mes")} style={botaoAba(aba === "mes")}>
          Vendas do mês
        </button>
        <button role="tab" aria-selected={aba === "pos"} type="button" onClick={() => setAba("pos")} style={botaoAba(aba === "pos")}>
          Acompanhamento
        </button>
      </div>

      {aba === "mes" && (
        <>
          <div style={{ display:"flex", gap:28, flexWrap:"wrap", marginBottom:14 }}>
            <div>
              <div style={{ fontFamily:MONO, fontSize:28, color:th.text }}>{brl(resumo.total)}</div>
              <div style={{ fontSize:13, color:th.muted }}>
                {resumo.qtd} venda{resumo.qtd !== 1 ? "s" : ""} · ticket {resumo.qtd ? brl(resumo.total / resumo.qtd) : "—"}
              </div>
            </div>
            {resumo.porOrigem.map(origin => (
              <div key={origin.origem}>
                <div style={{ fontFamily:MONO, fontSize:18, color:th.text }}>{brl(origin.valor)}</div>
                <div style={{ fontSize:13, color:th.muted }}>{origin.origem} · {origin.qtd}</div>
              </div>
            ))}
          </div>
          <div style={{ background:"#fff", border:`1px solid ${line}`, borderRadius:4 }}>
            {vendas.length === 0 && <div style={{ padding:16, color:th.muted, fontSize:14 }}>Nenhuma venda neste mês.</div>}
            {vendas.map(sale => (
              <button key={sale.id} type="button" className="nu-sales-card" onClick={() => sale.abrir?.()} style={{
                width:"100%", display:"grid", gridTemplateColumns:"minmax(0,1fr) auto", gap:10, alignItems:"center", textAlign:"left",
                background:"none", border:"none", borderBottom:`1px solid ${line}`, padding:"11px 16px",
                cursor:"pointer", fontFamily:"inherit", color:th.text,
              }}>
                <span style={{ minWidth:0 }}>
                  <span style={{ display:"flex", gap:8, alignItems:"baseline", flexWrap:"wrap" }}>
                    <span style={{ fontWeight:700, fontSize:15 }}>{sale.nome}</span>
                    {sale.origem && <span style={{
                      fontSize:11, fontWeight:700, color:th.accent, border:`1px solid ${line}`, borderRadius:99, padding:"1px 8px",
                    }}>{sale.origem}</span>}
                  </span>
                  <span style={{ display:"block", fontSize:12, color:th.muted, marginTop:2 }}>
                    {[dataCurta(sale.data), mostrarConsultor ? sale.consultor : null, sale.loja].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span style={{ fontFamily:MONO, fontSize:15 }}>{brl(sale.valor)}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {aba === "pos" && (
        <div style={{ display:"flex", gap:12, overflowX:"auto", paddingBottom:8, alignItems:"flex-start" }}>
          {COLUNAS.map(column => {
            const items = acompanhamento.filter(item => item.coluna === column.id);
            return (
              <div key={column.id} style={{ flex:"0 0 260px", minWidth:260 }}>
                <div style={{
                  display:"flex", justifyContent:"space-between", padding:"10px 12px", background:"#fff",
                  border:`1px solid ${line}`, borderTop:`3px solid ${column.cor}`, borderBottom:"none",
                }}>
                  <span style={{ fontWeight:700, fontSize:13 }}>{column.titulo}</span>
                  <span style={{ fontFamily:MONO, fontSize:12, color:th.muted }}>{items.length}</span>
                </div>
                <div style={{
                  background:th.sand || "#FAF8F3", border:`1px solid ${line}`, padding:8,
                  maxHeight:"calc(100vh - 280px)", overflowY:"auto",
                }}>
                  {items.length === 0 && <div style={{ color:th.muted, fontSize:12, textAlign:"center", padding:"16px 0" }}>Nenhum</div>}
                  {items.map(item => (
                    <button key={item.id} type="button" className="nu-sales-card" onClick={() => item.abrir?.()} style={{
                      width:"100%", textAlign:"left", background:"#fff", border:`1px solid ${line}`,
                      borderLeft:`3px solid ${item.atrasado ? "#B42318" : column.cor}`, borderRadius:4,
                      padding:"9px 10px", marginBottom:6, cursor:"pointer", fontFamily:"inherit", color:th.text,
                    }}>
                      <span style={{ display:"flex", justifyContent:"space-between", gap:6 }}>
                        <span style={{ fontWeight:700, fontSize:14 }}>{item.nome}</span>
                        <span style={{ fontFamily:MONO, fontSize:12 }}>{brl(item.valor)}</span>
                      </span>
                      <span style={{ display:"block", fontSize:12, color:th.muted, marginTop:3 }}>
                        {[item.origem, item.produto, mostrarConsultor ? item.consultor : null].filter(Boolean).join(" · ")}
                      </span>
                      {item.proximo && (
                        <span style={{
                          display:"block", fontSize:12, fontWeight:700, marginTop:4,
                          color:item.atrasado ? "#B42318" : th.text,
                        }}>{item.proximo}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}